// LIBRARIES //
import React from 'react';
import Auth from 'j-toker';
import { Formik, yupToFormErrors } from 'formik';
import * as Yup from 'yup';
import { connect } from 'react-redux';

// COMPONENTS //
import Location from './sections/Location';
import PartsContractorNeeded from './sections/PartsContractorNeeded';
import EnvironmentalTesting from './sections/EnvironmentalTesting';
import TicketDetails from './sections/TicketDetails';
import LinkToProject from './sections/LinkToProject';
import Comments from './sections/Comments';
import BannerMessage from './sections/BannerMessage';
import AttachPhotos from './sections/AttachPhotos';
import AssignmentAndScheduling from './sections/AssignmentAndScheduling';
import { RenderIfToggleOn } from 'components/shared/ToggleRenderer';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';

// ACTIONS //
import { showProperty } from 'actions/landlordPropertiesActions';
import { downloadPdf } from 'actions/maintenanceTicketActions';

// HELPERS //
import { getTranslatedString } from 'helpers/i18n';
import { toMomentDate, toMomentDateTime, daysFromNow } from 'helpers/Formatter';
import { featureEnabled } from 'helpers/ToggleBot';
import { hostIsCapreit } from 'helpers/CurrentWindowLocation';

import HeaderButtons from './sections/HeaderButtons';

const PROPOSED_SCHEDULED_DATE = 'proposed_scheduled_date';
class TicketForm extends React.Component {
  validateForm = async (values) => {
    const requiredErrorVerbiage = getTranslatedString('required');
    let validationSchema = {
      property_id: Yup.string().required(requiredErrorVerbiage),
      property_option: Yup.string().required(requiredErrorVerbiage),
      unit_id:
        values.property_option === 'unit'
          ? Yup.string().required(requiredErrorVerbiage)
          : Yup.string(),
      unit_option:
        values.property_option === 'unit'
          ? Yup.string().required(requiredErrorVerbiage)
          : Yup.string(),
      room_id:
        values.property_option === 'room' || values.unit_option === 'room'
          ? Yup.string().required(requiredErrorVerbiage)
          : Yup.string(),
      asset_id:
        values.property_option === 'asset' || values.unit_option === 'asset'
          ? Yup.string().required(requiredErrorVerbiage)
          : Yup.string(),
      environmental_testing: Yup.boolean().required(requiredErrorVerbiage),
      environmental_testing_result: values.environmental_testing
        ? Yup.boolean().required(requiredErrorVerbiage)
        : Yup.boolean(),
      abatement_date: Yup.date().nullable(),
      subject: Yup.string().required(requiredErrorVerbiage),
      ticket_description: Yup.string().required(requiredErrorVerbiage),
      priority: Yup.string().required(requiredErrorVerbiage),
      request_type: Yup.string().required(requiredErrorVerbiage),
      category: Yup.string().required(requiredErrorVerbiage),
      permission_to_enter: featureEnabled('ui.create_service_request.permission_to_enter')
        ? Yup.boolean().required(requiredErrorVerbiage)
        : Yup.boolean(),
      scheduled_starts_at_time: values.scheduled
        ? Yup.string().required(requiredErrorVerbiage)
        : Yup.string(),
      scheduled_starts_at_date: values.scheduled
        ? Yup.date().required(requiredErrorVerbiage).nullable()
        : Yup.date().nullable(),

      consent_to_entry_while_away: featureEnabled(
        'ui.create_service_request.consent_to_entry_while_away'
      )
        ? Yup.boolean().required(requiredErrorVerbiage)
        : Yup.boolean(),
      hide_from_tenant: Yup.boolean().required(requiredErrorVerbiage),
      suite_location: Yup.string().required(requiredErrorVerbiage),
    };
    const schema = Yup.object().shape(validationSchema);
    return schema.validate(values, { abortEarly: false }).catch((error) => {
      throw yupToFormErrors(error);
    });
  };

  componentDidMount() {
    this.props.showProperty(); // clears the current property incase it has the incorrect maintenance_dispatch flag
  }

  render() {
    const { formData, mode, imageMaxSize, readOnly, setProposedScheduledResponse } = this.props;

    const partsContractorNeededStatuses = [
      'Parts Needed',
      'Parts Needed Review',
      'Contractor Needed',
      'Contractor Needed Review',
    ];
    const partsContractorNeededReview = partsContractorNeededStatuses.includes(formData.status);
    const userIsMaintenance = Auth.user.type === 'maintenance';

    return (
      <React.Fragment>
        <Formik
          enableReinitialize
          initialValues={{
            id: formData.id || undefined,
            property_id: formData.property_id,
            property_option: formData.unit_id
              ? 'unit'
              : formData.asset_id
              ? 'asset'
              : formData.room_id
              ? 'room'
              : 'property',
            unit_option: formData.asset_id ? 'asset' : formData.room_id ? 'room' : 'unit',
            unit_id: formData.unit_id || undefined,
            room_id: formData.room_id || undefined,
            asset_id: formData.maintenance_asset_id || undefined,
            files: formData.files || [],
            photos: formData.photos || [],
            environmental_testing: ['positive', 'negative'].includes(
              formData.environmental_testing
            ),
            environmental_testing_result: formData.environmental_testing === 'positive',
            abatement_date:
              (formData.abatement_date && toMomentDate(formData.abatement_date)) || undefined,
            status: formData.status || undefined,
            previous_status: formData.previous_status || undefined,
            assign: formData.assign || undefined,
            assign_string:
              formData.assign &&
              `${formData.assign.type}$${formData.assign.value}$${formData.assign.label}`,
            scheduled_starts_at_date: formData.scheduled_starts_at
              ? formData.scheduled_starts_at_date
              : undefined,
            scheduled_starts_at_time: formData.scheduled_starts_at
              ? formData.scheduled_starts_at_time
              : undefined,
            scheduled_duration: formData.scheduled_duration || hostIsCapreit() ? 4 : 0.5,
            scheduled: formData.scheduled || undefined,
            proposed_scheduled:
              (formData.scheduled && formData.status === PROPOSED_SCHEDULED_DATE) || undefined,
            scheduled_at:
              (formData.scheduled_at && toMomentDate(formData.scheduled_at)) || undefined,
            completion_date:
              (formData.completion_date && toMomentDateTime(formData.completion_date)) || undefined,
            complete_by:
              (formData.complete_by && toMomentDateTime(formData.complete_by)) || undefined,
            travel_time_hour: formData.travel_time_hour || undefined,
            travel_time_minute: formData.travel_time_minute || undefined,
            worked_time_hour: formData.worked_time_hour || undefined,
            worked_time_minute: formData.worked_time_minute || undefined,
            subject: formData.subject,
            ticket_description: formData.ticket_description,
            priority: formData.priority || undefined,
            request_type: formData.request_type || undefined,
            category: formData.category || undefined,
            permission_to_enter: [true, false].includes(formData.permission_to_enter)
              ? formData.permission_to_enter
              : undefined,
            consent_to_entry_while_away: [true, false].includes(
              formData.consent_to_entry_while_away
            )
              ? formData.consent_to_entry_while_away
              : undefined,
            hide_from_tenant: [true, false].includes(formData.hide_from_tenant)
              ? formData.hide_from_tenant
              : undefined,
            internal_notes: formData.internal_notes || '',
            notes_to_tenant: '',
            suite_location: formData.suite_location || undefined,
            project_id: formData.project_id || undefined,
            is_tenant_damaged: formData.is_tenant_damaged || false,
            external_po_number: formData.external_po_number,
            subcategory: formData.subcategory || '',
            created_at: formData.created_at || toMomentDateTime(daysFromNow()),
            classification: formData.classification,
            tenant_note: formData.tenant_note || undefined,
            contractor_part_type: formData.contractor_part_type || undefined,
            responsible_for_action: formData.responsible_for_action || undefined,
            proposed_scheduled_at:
              (formData.proposed_scheduled_at && toMomentDate(formData.proposed_scheduled_at)) ||
              undefined,
          }}
          onSubmit={(values) => this.props.handleSubmit(values)}
          validate={this.validateForm}
        >
          {({ handleSubmit, ...rest }) => (
            <VerticalFormWrapper id={this.props.formName} onSubmit={handleSubmit}>
              <BannerMessage {...rest} userIsMaintenance={userIsMaintenance} />
              {partsContractorNeededReview && (
                <PartsContractorNeeded downloadPdf={this.props.downloadPdf} {...rest} />
              )}
              {mode === 'edit' && (
                <HeaderButtons
                  mode={mode}
                  showGeneratePDF={!partsContractorNeededReview}
                  formProps={rest}
                />
              )}
              <Location
                readOnly={readOnly}
                formProps={rest}
                viewDetails={this.props.mode === 'edit'}
              />
              <TicketDetails
                readOnly={readOnly}
                viewDetails={this.props.mode === 'edit'}
                {...rest}
              />
              <RenderIfToggleOn toggle="ui.maintenance.environmental_testing">
                <EnvironmentalTesting readOnly={readOnly} {...rest} />
              </RenderIfToggleOn>
              <AssignmentAndScheduling
                readOnly={readOnly}
                setIsLoading={this.props.setIsLoading}
                {...rest}
                mode={mode}
                property_maintenance_dispatch_edit={
                  formData.property && formData.property.maintenance_dispatch
                }
                setProposedScheduledResponse={setProposedScheduledResponse}
              />
              <RenderIfToggleOn toggle="ui.maintenance.link_to_project">
                <LinkToProject readOnly={readOnly} {...rest} linkedProject={formData.project} />
              </RenderIfToggleOn>
              <AttachPhotos readOnly={readOnly} imageMaxSize={imageMaxSize} {...rest} />
              <Comments readOnly={readOnly} {...rest} mode={mode} />
            </VerticalFormWrapper>
          )}
        </Formik>
      </React.Fragment>
    );
  }
}

export default connect(null, { showProperty, downloadPdf })(TicketForm);
